import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Box, Divider, Grid, Paper as CustomPaper, Typography, Tooltip, makeStyles, createStyles } from '@material-ui/core';
import { usePaperStyles } from '../hooks/usePaperStyles';
import { AuthorizeInputType, SearchResult } from '../types/api';
import { getMDYFormat, getFullName, getLastSSN, sanitizeInput, getFormattedDate, getFormattedDateInDaysAgo, getRegion } from '../utils/AccountDetailsUtil';
import Button from './Button';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { ApiStatus, authorizeByPECUser } from '../features/search/api';
import { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { ClientConfigContext } from '../config/clientConfig';
import { SessionContext } from '../session/sessionContext';
import { CardType } from '../types/cardTypes';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { EhrRegionStyles, Theme } from '../types/serverConfig';

type mrn = {
    id: string;
    system: string;
};

export type PaperAuthorizationProps = {
    onAuthorize: (account: SearchResult, newPassword?: string) => void;
    isAuthorizing: boolean;
    onAuthorizing: (isAuthorizing: boolean) => void;
};

export type PaperProps = {
    variant: 'outlined' | undefined;
    inputData: SearchResult;
    mrns: mrn[];
    onUnlink: any;
    verifiedWith: string;
} & (
    | {
          cardType: CardType.NormalCard;
          onAuthorize?: never;
          isAuthorizing?: never;
          onAuthorizing?: never;
      }
    | ({ cardType: CardType.LinkedCard } & PaperAuthorizationProps)
);

interface styleProps {
    region?: string;
}

/* Gets the Card helmet back ground color based on the region text passed */
const getEHRHelmetColor = (theme: Theme, region?: string) => {
    return theme.palette.regionColor?.find((rc: EhrRegionStyles) => rc.name.toLowerCase() === region?.toLowerCase())?.helmetColor;
};

/* Gets the Card MRN back ground color based on the region text passed */
const getMRNColor = (theme: Theme, region?: string) => {
    return theme.palette.regionColor?.find((rc: EhrRegionStyles) => rc.name.toLowerCase() === region?.toLowerCase())?.mrnColor;
};

/* Defines card heading styles for helmet and mrn based on the region */
const cardHeadingStyles = makeStyles((theme: Theme) =>
    createStyles({
        helmetColor: (props: styleProps) => ({
            backgroundColor: getEHRHelmetColor(theme, props.region),
            borderColor: getEHRHelmetColor(theme, props.region),
        }),
        mrnColor: (props: styleProps) => ({
            backgroundColor: getMRNColor(theme, props.region),
            borderColor: getMRNColor(theme, props.region),
        }),
    }),
);

const useStaleBannerStyles = makeStyles(() =>
    createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            backgroundColor: '#FDF0E6',
            border: '1px solid #E9A67A',
            borderRadius: 8,
            padding: '12px 16px',
            color: '#C45D2B',
        },
        icon: {
            marginRight: 12,
            fontSize: 22,
            color: '#C45D2B',
            flexShrink: 0,
        },
        message: {
            fontWeight: 700,
            fontSize: 14,
            lineHeight: '20px',
            color: '#C45D2B',
            minWidth: 0,
        },
        container: {
            maxWidth: '100%',
            minWidth: 0,
        },
    }),
);

export const Paper = ({ variant, inputData, mrns, cardType, onUnlink, verifiedWith, onAuthorize, isAuthorizing, onAuthorizing }: PaperProps) => {
    const paperStyles = usePaperStyles();
    const staleBannerStyles = useStaleBannerStyles();
    const [data, setData] = useState<SearchResult>(inputData);
    const [verificationWith, setVerificationWith] = useState(verifiedWith);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setData(inputData);
        setVerificationWith(verifiedWith);
    }, [inputData, verifiedWith]);

    const renderStaleBanner = () => (
        <Box className={staleBannerStyles.root} role='alert'>
            <ReportProblemOutlinedIcon className={staleBannerStyles.icon} />
            <Typography className={staleBannerStyles.message}>MRN not found in Epic. Unlink it and link the correct one.</Typography>
        </Box>
    );

    const sessionId = useContext(SessionContext);
    const clientConfiguration = useContext(ClientConfigContext);
    const msalContext = useMsal();
    const instance = msalContext.instance;
    const accounts = msalContext.accounts as AccountInfo[];

    const getCardHeading = (mrn: string, classes: ClassNameMap<'helmetColor' | 'mrnColor'>, region?: string) => {
        return (
            <Fragment key={mrn}>
                <Grid item xs={1}>
                    <Typography variant='subtitle1' className={`accountDetailsHelmet ${classes.helmetColor}`}>
                        {region}
                    </Typography>
                </Grid>
                <Grid item className='mr10'>
                    <Typography variant='subtitle1' className={`accountDetailsMRN ${classes.mrnColor}`}>
                        <span>{mrn}</span>
                    </Typography>
                </Grid>
            </Fragment>
        );
    };
    const getCardHelmet = () => {
        if (cardType === CardType.NormalCard) {
            return (
                <>
                    <Grid container alignItems='flex-start' direction='column' className='cardHeader'>
                        <Grid item>
                            <Typography variant='subtitle2' className='accountDetailsHeader'>
                                MRN
                            </Typography>
                        </Grid>
                        {data.system ? (
                            <Grid container alignItems='flex-start' direction='row' className='mt5'>
                                {mrns?.length > 0
                                    ? mrns?.map((mrnData) => {
                                          const regionText = getRegion(mrnData.system, clientConfiguration);
                                          return getCardHeading(mrnData.id, cardHeadingStyles({ region: regionText }), regionText);
                                      })
                                    : () => {
                                          const regionText = getRegion(data.system, clientConfiguration);
                                          getCardHeading('NON-Patient Proxy', cardHeadingStyles({ region: regionText }), regionText);
                                      }}
                            </Grid>
                        ) : (
                            <Grid item>
                                <Typography variant='subtitle1' className='mt10'>
                                    --
                                </Typography>
                            </Grid>
                        )}
                        {data.isStale && (
                            <Grid item xs={12} className={`mb9 ${staleBannerStyles.container}`} style={{ paddingTop: '10px' }}>
                                {renderStaleBanner()}
                            </Grid>
                        )}
                    </Grid>
                </>
            );
        } else {
            const region = getRegion(data.system, clientConfiguration);
            const cardHeadingClasses = cardHeadingStyles({ region: region });
            return (
                <>
                    <Grid container alignItems='center' direction='row' className={`linkedRecordHelmetRegion ${cardHeadingClasses.helmetColor}`}>
                        <Grid item xs>
                            <Typography variant='h5' className='mrnRegion'>
                                {region}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Typography variant='subtitle1' className='ml40'>
                                {data?.mrn && data?.mrn !== 'NON-PATIENT PROXY' ? (
                                    <>
                                        <span className='accountDetailsHeader'>MRN:</span>
                                        <span className='accountDetailsValue'>{data?.mrn}</span>
                                    </>
                                ) : (
                                    <span className='accountDetailsValue'>NON-PATIENT PROXY</span>
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </>
            );
        }
    };

    const getAccountAuthItem = () => {
        if (!data?.requireMigrateAccount) {
            const authorization = data?.accountAuthorizations.find((auth) => auth.authorizationSystem === data.system);
            return (
                <Grid item className='accountAuthItem'>
                    {data.requireCreateAccount && (
                        <Grid item>
                            <Typography className='accountAuthMessage'>
                                This account is linked, however, authorization process is not complete. Please authorize this account now.
                            </Typography>
                        </Grid>
                    )}
                    <Grid container item xs alignItems='flex-start'>
                        <Grid item xs={6}>
                            {data.requireCreateAccount || data.migratedInactive ? (
                                <>
                                    <Button
                                        type='button'
                                        variant='contained'
                                        color='secondary'
                                        fontSize='12px'
                                        minWidthOverride='185px !important'
                                        disabled={isAuthorizing}
                                        onClick={handleAuthorizeClick}
                                    >
                                        AUTHORIZE ACCOUNT
                                    </Button>
                                    {renderErrorMessage()}
                                </>
                            ) : (
                                <Typography variant='subtitle2' className='accountDetailsHeader'>
                                    Link updated: {sanitizeInput(getMDYFormat(authorization?.authorizationDate, false))}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={6} className='unLink'>
                            <Button type='button' variant='outlined' color='secondary' fontSize='12px' onClick={openLinkRemovalModal}>
                                UNLINK
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            );
        }
    };

    // TODO: WHY NOT sanitizeInput the input fields once????????
    const getDisplayItemAccordion = (label: string, input: string | undefined) => {
        const values = input?.split('|');
        if (values && values.length > 1) {
            return (
                <>
                    <Accordion square>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                            <Typography noWrap variant='subtitle2' className='accountDetailsHeader'>
                                {`${label}`}
                                <span className='boldText'> ({values.length})</span>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container direction='column' className='mt10'>
                                {values.map((myChartName) => {
                                    return (
                                        <Fragment key={myChartName}>
                                            <Grid item>
                                                {label == 'MyChart username' ? (
                                                    <Tooltip title={myChartName} placement='top-start'>
                                                        <Typography noWrap variant='subtitle2' className='accountDetailsHeader mt3'>
                                                            <span className='boldText'> {myChartName.split(' ')[0]} </span> {myChartName.split(' ')[1]}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography noWrap variant='subtitle1' className='accountDetailsValue mt3'>
                                                        {myChartName}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Fragment>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                </>
            );
        } else {
            return (
                <>
                    <Typography variant='subtitle2' className='accountDetailsHeader'>
                        {label}
                    </Typography>
                    {label == 'MyChart username' ? (
                        <Tooltip title={sanitizeInput(input)} placement='top-start'>
                            <Typography noWrap variant='subtitle1' className='accountDetailsValue'>
                                {sanitizeInput(input)}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Typography noWrap variant='subtitle1' className='accountDetailsValue'>
                            {sanitizeInput(input)}
                        </Typography>
                    )}
                </>
            );
        }
    };

    // TODO: Generic Paper component knows about a open link removal model
    const openLinkRemovalModal = () => {
        const fullName = sanitizeInput(getFullName(data?.ehrDemographics?.firstName, data?.ehrDemographics?.lastName));

        onUnlink(fullName, data.mychartUserName, data.mrn, data.system);
    };

    const handleAuthorizeClick = async () => {
        onAuthorizing(true);
        setErrorMessage('');
        const request: AuthorizeInputType = {
            healthSystemId: data.healthSystemId,
            ssoUserId: data.azureId,
            ehrSystem: data.system,
            authorizationDetails: {
                mrn: data.mrn,
                adminUserID: accounts?.[0].username,
            },
        };
        const result = await authorizeByPECUser(request, instance, accounts, clientConfiguration, sessionId);
        onAuthorizing(false);
        if (result.status === ApiStatus.Success) {
            const accountAuthorizations = [...data.accountAuthorizations];
            const authorization = accountAuthorizations.find((auth) => auth.authorizationSystem === data.system);
            if (!authorization) {
                accountAuthorizations.push({
                    authorizationDate: getFormattedDate(new Date().toString()),
                    authorizationDetails: {
                        isNonPatientProxy: false,
                        myChartSystem: data.system,
                        myChartUsername: undefined,
                    },
                    authorizationMethod: 'PEC Linked Record Entry',
                    authorizationSystem: data.system,
                });
            }
            setVerificationWith(authorization?.authorizationMethod ?? 'PEC Linked Record Entry');
            const authorizedAccount: SearchResult = {
                ...data,
                accountAuthorizations,
                requireCreateAccount: false,
                requireMigrateAccount: false,
                migratedInactive: result.data.newPassword ? false : data.migratedInactive,
            };
            setData(authorizedAccount);
            onAuthorize(authorizedAccount, result.data.newPassword);
        } else {
            setErrorMessage('Account authorization has failed');
        }
    };
    const renderErrorMessage = () => {
        return (
            errorMessage && (
                <Typography variant='subtitle1' color='error' align='right'>
                    {errorMessage}
                </Typography>
            )
        );
    };
    const renderPaper = () => {
        const authorization = data.accountAuthorizations?.find((auth) => auth.authorizationSystem === data.system);
        return (
            <CustomPaper classes={{ ...paperStyles }} variant={variant}>
                {getCardHelmet()}
                <Divider variant='middle' />
                {cardType === CardType.NormalCard ? (
                    // This is the card that is displayed when we search and find a result
                    <Grid container alignItems='flex-start' direction='row' className='normalCard'>
                        <Grid container item xs alignItems='flex-start' direction='column'>
                            <Grid item className='mb9'>
                                {getDisplayItemAccordion('Name', getFullName(data?.ehrDemographics?.firstName, data?.ehrDemographics?.lastName))}
                            </Grid>
                            <Grid item className='mb9'>
                                {getDisplayItemAccordion('Last login', data?.userLastSignedInDate)}
                            </Grid>
                            <Grid item className='myChartName'>
                                {getDisplayItemAccordion('MyChart username', data?.mychartUserName)}
                            </Grid>
                            <Grid item className='mb9'>
                                {getDisplayItemAccordion('Email', data?.ehrDemographics?.email)}
                            </Grid>
                        </Grid>
                        <Grid container item xs alignItems='flex-start' direction='column'>
                            <Grid item className='mb9'>
                                {getDisplayItemAccordion('Sign-in name', data?.ssoAccountUsername)}
                            </Grid>
                            <Grid item className='mb9'>
                                {getDisplayItemAccordion('Last 4 SSN', getLastSSN(data?.ehrDemographics?.ssn))}
                            </Grid>
                            <Grid item className='mb9'>
                                {getDisplayItemAccordion('DOB', getMDYFormat(data?.ehrDemographics?.dateOfBirth))}
                            </Grid>
                            {(data?.migratedInactive || data?.accountAuthorizations?.length == 1) && (
                                <Grid item className='mb9'>
                                    {getDisplayItemAccordion('Status', data.migratedInactive ? 'Migrated (Setup Needed)' : verificationWith)}
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    // This is the card displayed for each EHR record
                    <Grid container alignItems='flex-start' direction='row' className='linkedCard' style={{ padding: '15px' }}>
                        {data?.isStale && (
                            <Grid container item xs={12} className={`mb9 ${staleBannerStyles.container}`}>
                                {renderStaleBanner()}
                            </Grid>
                        )}
                        <Grid container item xs alignItems='flex-start' direction='column'>
                            <Grid item className='mb9'>
                                <Typography variant='subtitle2' className='accountDetailsValue'>
                                    {getFullName(data?.ehrDemographics?.firstName, data?.ehrDemographics?.lastName)}
                                </Typography>
                                <Typography noWrap variant='subtitle1' className='accountDetailsHeader'>
                                    {sanitizeInput(data?.mychartUserName)}
                                </Typography>
                            </Grid>
                            {!!authorization && (
                                <Grid item className='mb9'>
                                    <Typography variant='subtitle2' className='accountDetailsValue'>
                                        Verified on
                                    </Typography>
                                    <Typography variant='subtitle1' className='accountDetailsHeader'>
                                        {getFormattedDate(authorization.authorizationDate)}
                                    </Typography>
                                    <Typography variant='subtitle1' className='accountDetailsHeader mt-7'>
                                        ({getFormattedDateInDaysAgo(authorization.authorizationDate)})
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                        <Grid container item xs alignItems='flex-start' direction='column'>
                            <Grid item className='ssnComponent '>
                                <Typography variant='subtitle2'>
                                    <span className='accountDetailsHeader'>Last 4 SSN:</span>
                                    <span className='accountDetailsValue'>{sanitizeInput(getLastSSN(data?.ehrDemographics?.ssn))}</span>
                                </Typography>
                                <Typography variant='subtitle2' className='accountDetailsHeader'>
                                    <span className='accountDetailsHeader'>DOB:</span>
                                    <span className='accountDetailsValue'>{sanitizeInput(getMDYFormat(data?.ehrDemographics?.dateOfBirth))}</span>
                                </Typography>
                            </Grid>
                            {!!authorization && (
                                <Grid item className='mb30'>
                                    <Typography variant='subtitle2' className='accountDetailsValue'>
                                        Verified with
                                    </Typography>
                                    <Typography variant='subtitle1' className='accountDetailsHeader'>
                                        {sanitizeInput(verificationWith)}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                        {getAccountAuthItem()}
                    </Grid>
                )}
            </CustomPaper>
        );
    };

    return renderPaper();
};

export default Paper;
